import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

export interface UseResizableColumnOptions {
  initial: number;
  min: number;
  max: number;
  storageKey: string;
  unit: "percent" | "pixel";
  /** Set when the tracked column sits to the right of its drag handle, so dragging right should shrink it (not grow). Defaults to false. */
  invert?: boolean;
}

export interface UseResizableColumnResult {
  width: number;
  isDragging: boolean;
  handleMouseDown: (event: React.MouseEvent) => void;
}

export function useResizableColumn(options: UseResizableColumnOptions): UseResizableColumnResult {
  const { initial, min, max, storageKey, unit, invert = false } = options;
  const [width, setWidth] = useState<number>(initial);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const dragStartX = useRef<number>(0);
  const dragStartWidth = useRef<number>(initial);
  const widthRef = useRef<number>(initial);
  widthRef.current = width;

  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) return;
    const saved = window.localStorage.getItem(storageKey);
    if (saved === null) return;
    const parsed = parseInt(saved, 10);
    if (!Number.isFinite(parsed)) return;
    if (parsed >= min && parsed <= max) setWidth(parsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    setIsDragging(true);
    dragStartX.current = event.clientX;
    dragStartWidth.current = widthRef.current;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = (event.clientX - dragStartX.current) * (invert ? -1 : 1);
      let next: number;
      if (unit === "percent") {
        const deltaPercentage = (deltaX / window.innerWidth) * 100;
        next = Math.max(min, Math.min(max, dragStartWidth.current + deltaPercentage));
      } else {
        next = Math.max(min, Math.min(max, dragStartWidth.current + deltaX));
      }
      if (next !== widthRef.current) setWidth(next);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(storageKey, Math.round(widthRef.current).toString());
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, min, max, unit, storageKey, invert]);

  return { width, isDragging, handleMouseDown };
}
